/**
 * E2E Flow Renderer
 * Issue #312: React Flow renderer for End-to-End User Interaction Flow Diagrams
 *
 * Renders complete user flows from frontend interaction to database operations.
 */

import { useCallback, useMemo, useEffect, forwardRef, useImperativeHandle, useRef, useState, memo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Node,
  Edge,
  BackgroundVariant,
  Panel,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';

import { e2eFlowNodeTypes, getStepTypeColors, STEP_TYPE_LABELS } from './E2EFlowNodes';
import {
  convertE2EFlowToReactFlow,
  convertE2EFlowToSwimlane,
  calculateFlowStats,
  type E2EFlowData,
  type LayoutMode,
} from './e2eFlowConverter';
import { swimlaneEdgeTypes } from '../../edges/SwimlaneCrossColumnEdge';
import type { E2EFlowPath, FlowStepType } from '@veodiagram/shared';

// Re-export E2EFlowData and LayoutMode from converter
export type { E2EFlowData, LayoutMode } from './e2eFlowConverter';

// ============= Types =============

// Issue #389: Position type for node layout persistence
export type NodePosition = { x: number; y: number };
export type NodePositions = Record<string, NodePosition>;

interface E2EFlowRendererProps {
  data: E2EFlowData;
  className?: string;
  readOnly?: boolean;
  editable?: boolean;  // Issue #389: Allow node dragging (separate from readOnly for connections)
  customPositions?: NodePositions;  // Issue #389: Pre-loaded custom positions
  layoutMode?: LayoutMode;  // Issue #427: Layout mode (sequential or swimlane)
  onNodeClick?: (node: Node) => void;
  onEdgeClick?: (edge: Edge, sourceNode: Node | undefined, targetNode: Node | undefined) => void;  // Issue #434: Include source/target nodes
  onPathSelect?: (path: E2EFlowPath) => void;
  onLayoutChange?: (positions: NodePositions) => void;  // Issue #389: Called when nodes are moved
  onSaveLayout?: () => void;  // Issue #389: Called when user clicks Save Layout
  onLayoutModeChange?: (mode: LayoutMode) => void;  // Issue #427: Called when layout mode changes
}

export interface E2EFlowRendererHandle {
  fitView: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  getViewportElement: () => HTMLElement | null;
  applyAutoLayout: () => void;
  selectPath: (pathId: string) => void;
  focusNode: (nodeId: string) => void;  // Issue #354: Focus/center on a specific node
  getNodePositions: () => NodePositions;  // Issue #389: Get current node positions
  resetToAutoLayout: () => void;  // Issue #389: Reset to auto-calculated positions
  hasUnsavedLayoutChanges: () => boolean;  // Issue #389: Check for unsaved changes
  clearLayoutChanges: () => void;  // Issue #389: Clear the layout modified indicator after save
  // Issue #404: Direction control methods
  getDirection: () => LayoutDirection;
  setDirection: (direction: LayoutDirection) => void;
  // Issue #404: Legend control methods
  getShowLegend: () => boolean;
  setShowLegend: (show: boolean) => void;
  toggleLegend: () => void;
  // Issue #404: Node finder control methods
  getShowNodeFinder: () => boolean;
  setShowNodeFinder: (show: boolean) => void;
  toggleNodeFinder: () => void;
  // Issue #404: Layout status
  isLayouting: () => boolean;
  // Issue #427: Swimlane layout methods
  getLayoutMode: () => LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
  toggleLayoutMode: () => void;
}

// Layout direction type
// Issue #404: Export for external use
export type LayoutDirection = 'TB' | 'LR' | 'BT' | 'RL';

// Issue #404: Direction labels moved to E2EFlowsPage toolbar

// ============= Component =============

function E2EFlowRendererInner(
  {
    data,
    className = '',
    readOnly = true,
    editable = true,  // Issue #389: Enable dragging by default
    customPositions,
    layoutMode: propLayoutMode = 'sequential',  // Issue #427: Default to sequential
    onNodeClick,
    onEdgeClick,
    onPathSelect,
    onLayoutChange,
    onSaveLayout: _onSaveLayout,  // Reserved for future use - save handled in parent toolbar
    onLayoutModeChange,  // Issue #427: Layout mode change callback
  }: E2EFlowRendererProps,
  ref: React.Ref<E2EFlowRendererHandle>
) {
  const reactFlowInstance = useReactFlow();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLayouting, setIsLayouting] = useState(false);
  const [selectedPathId, setSelectedPathId] = useState<string | null>(null);
  // Issue #427: Layout mode state (swimlane or sequential)
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>(propLayoutMode);
  const [activeStepTypes, setActiveStepTypes] = useState<Set<FlowStepType>>(new Set());
  const [showLegend, setShowLegend] = useState(true);
  const [showNodeFinder, setShowNodeFinder] = useState(false);  // Issue #354: Node finder panel
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);  // Issue #354: Highlighted node
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>('TB');
  const prevLayoutDirectionRef = useRef<LayoutDirection>('TB');

  // Issue #389: Track layout changes
  const [hasLayoutChanges, setHasLayoutChanges] = useState(false);
  const originalPositionsRef = useRef<NodePositions>({});
  const customPositionsRef = useRef<NodePositions | undefined>(customPositions);

  // Keep refs in sync with props
  const dataRef = useRef(data);
  useEffect(() => {
    customPositionsRef.current = customPositions;
    dataRef.current = data;
  }, [customPositions, data]);

  // Issue #427: Sync internal layoutMode state with prop changes
  useEffect(() => {
    setLayoutModeState(propLayoutMode);
  }, [propLayoutMode]);

  // Convert E2E flow data to React Flow format
  // Issue #389: Apply custom positions if provided
  // Issue #427: Convert data based on layout mode
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    // Use appropriate converter based on layout mode
    if (layoutMode === 'swimlane') {
      const result = convertE2EFlowToSwimlane(data, {
        customPositions: customPositionsRef.current,
        resizable: editable,
      });
      // Store original auto-calculated positions for reset functionality
      const autoPositions: NodePositions = {};
      const autoResult = convertE2EFlowToSwimlane(data, { resizable: editable });
      autoResult.nodes.forEach(node => {
        autoPositions[node.id] = { ...node.position };
      });
      originalPositionsRef.current = autoPositions;
      return result;
    } else {
      const result = convertE2EFlowToReactFlow(data, customPositionsRef.current);
      // Store original auto-calculated positions for reset functionality
      const autoPositions: NodePositions = {};
      const autoResult = convertE2EFlowToReactFlow(data);
      autoResult.nodes.forEach(node => {
        autoPositions[node.id] = { ...node.position };
      });
      originalPositionsRef.current = autoPositions;
      return result;
    }
  }, [data, layoutMode, editable]);

  // Calculate flow statistics
  const stats = useMemo(() => calculateFlowStats(data), [data]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Refs for stable callbacks — always point to latest state
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  // Get available step types from nodes
  const availableStepTypes = useMemo(() => {
    const types = new Set<FlowStepType>();
    nodes.forEach(node => {
      const stepType = node.data?.type as FlowStepType | undefined;
      if (stepType && stepType in STEP_TYPE_LABELS) {
        types.add(stepType);
      }
    });
    return Array.from(types);
  }, [nodes]);

  // Filter nodes by selected path and step types, apply highlight
  const filteredNodes = useMemo(() => {
    let filtered = nodes;

    // Filter by selected path
    if (selectedPathId) {
      filtered = filtered.filter(node => node.id.startsWith(selectedPathId));
    }

    // Filter by step types
    if (activeStepTypes.size > 0) {
      filtered = filtered.filter(node => {
        const stepType = node.data?.type as FlowStepType | undefined;
        return stepType && activeStepTypes.has(stepType);
      });
    }

    // Issue #354: Apply highlight style to focused node
    if (highlightedNodeId) {
      filtered = filtered.map(node => {
        if (node.id === highlightedNodeId) {
          return {
            ...node,
            style: {
              ...node.style,
              boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.8), 0 0 20px rgba(59, 130, 246, 0.4)',
              transform: 'scale(1.05)',
              transition: 'all 0.3s ease-in-out',
            },
          };
        }
        return node;
      });
    }

    return filtered;
  }, [nodes, selectedPathId, activeStepTypes, highlightedNodeId]);

  // Filter edges to only include those between visible nodes
  const filteredEdges = useMemo(() => {
    const visibleNodeIds = new Set(filteredNodes.map(n => n.id));
    return edges.filter(edge =>
      visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
    );
  }, [edges, filteredNodes]);

  // Refs for stable applyAutoLayout callback
  const filteredNodesRef = useRef(filteredNodes);
  filteredNodesRef.current = filteredNodes;
  const filteredEdgesRef = useRef(filteredEdges);
  filteredEdgesRef.current = filteredEdges;

  // Update nodes/edges when data changes
  // Issue #389: Use a stable data ID to prevent unnecessary resets during drag
  const dataId = useMemo(() => {
    // Create a stable identifier from the data content
    return data.paths.map(p => p.id).join(',');
  }, [data.paths]);

  useEffect(() => {
    // Issue #389: Include customPositions to preserve user-arranged layout
    // Issue #427: Use appropriate converter based on layout mode
    // Use refs to get current values without triggering re-runs during drag
    let newNodes, newEdges;
    if (layoutMode === 'swimlane') {
      const result = convertE2EFlowToSwimlane(dataRef.current, {
        customPositions: customPositionsRef.current,
        resizable: editable,
      });
      newNodes = result.nodes;
      newEdges = result.edges;
    } else {
      const result = convertE2EFlowToReactFlow(dataRef.current, customPositionsRef.current);
      newNodes = result.nodes;
      newEdges = result.edges;
    }
    setNodes(newNodes);
    setEdges(newEdges);
    setSelectedPathId(null);
    setActiveStepTypes(new Set());
    // Issue #389: Reset layout change tracking when data actually changes
    setHasLayoutChanges(false);
    // Fit view after layout mode change
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.2 });
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataId, layoutMode, setNodes, setEdges, editable]); // Re-run when dataId or layoutMode changes

  // Handle new connections
  const onConnect = useCallback(
    (connection: Connection) => {
      if (readOnly) return;
      setEdges((eds) => addEdge(connection, eds));
    },
    [setEdges, readOnly]
  );

  // Handle node click
  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onNodeClick?.(node);
    },
    [onNodeClick]
  );

  // Handle edge click - Issue #434: Include source/target nodes for edge detail panel
  const handleEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      const sourceNode = nodesRef.current.find(n => n.id === edge.source);
      const targetNode = nodesRef.current.find(n => n.id === edge.target);
      onEdgeClick?.(edge, sourceNode, targetNode);
    },
    [onEdgeClick]
  );

  // Issue #389: Handle node drag stop - capture final position
  // Uses nodesRef to avoid recreating this callback every frame during drag,
  // which would disrupt React Flow's internal drag state tracking.
  const handleNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      // Notify parent of position change
      if (onLayoutChange) {
        const positions: NodePositions = {};
        nodesRef.current.forEach(n => {
          positions[n.id] = { ...n.position };
        });
        // Update with the dragged node's new position
        positions[node.id] = { ...node.position };
        onLayoutChange(positions);
      }
    },
    [onLayoutChange]
  );

  // Issue #389: Get current node positions
  const getNodePositions = useCallback((): NodePositions => {
    const positions: NodePositions = {};
    nodesRef.current.forEach(node => {
      positions[node.id] = { ...node.position };
    });
    return positions;
  }, []);

  // Issue #389: Reset to auto-calculated layout
  const resetToAutoLayout = useCallback(() => {
    const resetNodes = nodesRef.current.map(node => ({
      ...node,
      position: originalPositionsRef.current[node.id] || node.position,
    }));
    setNodes(resetNodes);
    setHasLayoutChanges(false);
    if (onLayoutChange) {
      onLayoutChange(originalPositionsRef.current);
    }
    // Fit view after reset
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.2 });
    }, 50);
  }, [setNodes, onLayoutChange, reactFlowInstance]);

  // Select a path
  const selectPath = useCallback((pathId: string) => {
    setSelectedPathId(prev => prev === pathId ? null : pathId);
    const path = data.paths.find(p => p.id === pathId);
    if (path) {
      onPathSelect?.(path);
    }
  }, [data.paths, onPathSelect]);

  // Toggle step type filter
  const toggleStepType = useCallback((type: FlowStepType) => {
    setActiveStepTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSelectedPathId(null);
    setActiveStepTypes(new Set());
  }, []);

  // Apply dagre auto layout with direction
  const applyAutoLayout = useCallback(() => {
    setIsLayouting(true);
    try {
      const g = new dagre.graphlib.Graph();
      g.setGraph({
        rankdir: layoutDirection,
        nodesep: 60,
        ranksep: 80,
        marginx: 50,
        marginy: 50,
      });
      g.setDefaultEdgeLabel(() => ({}));

      // Add nodes with dimensions (use refs for stable callback)
      filteredNodesRef.current.forEach((node) => {
        g.setNode(node.id, {
          width: 200,
          height: 80,
        });
      });

      // Add edges
      filteredEdgesRef.current.forEach((edge) => {
        g.setEdge(edge.source, edge.target);
      });

      // Run dagre layout
      dagre.layout(g);

      // Apply calculated positions
      const layoutedNodes = nodesRef.current.map((node) => {
        const nodeWithPosition = g.node(node.id);
        if (nodeWithPosition) {
          return {
            ...node,
            position: {
              x: nodeWithPosition.x - 100,
              y: nodeWithPosition.y - 40,
            },
          };
        }
        return node;
      });

      setNodes(layoutedNodes);

      // Issue #389: Clear unsaved changes after auto layout
      setHasLayoutChanges(false);

      // Issue #389: Notify parent of new positions
      if (onLayoutChange) {
        const positions: NodePositions = {};
        layoutedNodes.forEach(node => {
          positions[node.id] = { ...node.position };
        });
        onLayoutChange(positions);
      }

      // Fit view after layout
      setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.2 });
      }, 50);
    } catch (error) {
      console.error('Failed to apply auto layout:', error);
    } finally {
      setIsLayouting(false);
    }
  }, [setNodes, reactFlowInstance, layoutDirection, onLayoutChange]);

  // Re-layout when direction changes
  useEffect(() => {
    if (prevLayoutDirectionRef.current !== layoutDirection) {
      prevLayoutDirectionRef.current = layoutDirection;
      applyAutoLayout();
    }
  }, [layoutDirection, applyAutoLayout]);

  // Issue #436: Listen for edge-label-click events from clickable labels
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onEdgeClick) return;

    const handleEdgeLabelClick = (e: Event) => {
      const customEvent = e as CustomEvent<{ edge: Edge; sourceNode?: Node; targetNode?: Node }>;
      const { edge, sourceNode, targetNode } = customEvent.detail;
      onEdgeClick(edge, sourceNode, targetNode);
    };

    container.addEventListener('edge-label-click', handleEdgeLabelClick);
    return () => {
      container.removeEventListener('edge-label-click', handleEdgeLabelClick);
    };
  }, [onEdgeClick]);

  // Issue #354: Focus on a specific node by centering viewport on it
  const focusNode = useCallback((nodeId: string) => {
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (node) {
      // Set highlighted node for visual feedback
      setHighlightedNodeId(nodeId);
      // Clear highlight after 2 seconds
      setTimeout(() => setHighlightedNodeId(null), 2000);
      // Center on the node with zoom level 1.5 for better visibility
      reactFlowInstance.setCenter(
        node.position.x + 100, // offset for node width/2
        node.position.y + 40,  // offset for node height/2
        { zoom: 1.5, duration: 500 }
      );
    }
  }, [reactFlowInstance]);

  // Issue #427: Set layout mode with callback
  // Sequential mode always uses top-to-bottom direction
  const setLayoutMode = useCallback((mode: LayoutMode) => {
    setLayoutModeState(mode);
    if (mode === 'sequential') {
      setLayoutDirection('TB');
    }
    onLayoutModeChange?.(mode);
  }, [onLayoutModeChange]);

  // Issue #427: Toggle between sequential and swimlane
  const toggleLayoutMode = useCallback(() => {
    const newMode = layoutMode === 'sequential' ? 'swimlane' : 'sequential';
    setLayoutMode(newMode);
  }, [layoutMode, setLayoutMode]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    fitView: () => reactFlowInstance.fitView({ padding: 0.2 }),
    zoomIn: () => reactFlowInstance.zoomIn(),
    zoomOut: () => reactFlowInstance.zoomOut(),
    getViewportElement: () =>
      containerRef.current?.querySelector('.react-flow__viewport') as HTMLElement | null,
    applyAutoLayout,
    selectPath,
    focusNode,  // Issue #354
    getNodePositions,  // Issue #389
    resetToAutoLayout,  // Issue #389
    hasUnsavedLayoutChanges: () => hasLayoutChanges,  // Issue #389
    clearLayoutChanges: () => setHasLayoutChanges(false),  // Issue #389: Clear after save
    // Issue #404: Direction control
    getDirection: () => layoutDirection,
    setDirection: (newDirection: LayoutDirection) => setLayoutDirection(newDirection),
    // Issue #404: Legend control
    getShowLegend: () => showLegend,
    setShowLegend: (show: boolean) => setShowLegend(show),
    toggleLegend: () => setShowLegend(prev => !prev),
    // Issue #404: Node finder control
    getShowNodeFinder: () => showNodeFinder,
    setShowNodeFinder: (show: boolean) => setShowNodeFinder(show),
    toggleNodeFinder: () => setShowNodeFinder(prev => !prev),
    // Issue #404: Layout status
    isLayouting: () => isLayouting,
    // Issue #427: Swimlane layout methods
    getLayoutMode: () => layoutMode,
    setLayoutMode,
    toggleLayoutMode,
  }));

  return (
    <div ref={containerRef} className={`h-full w-full ${className}`}>
      <ReactFlow
        nodes={filteredNodes}
        edges={filteredEdges}
        onNodesChange={editable || !readOnly ? onNodesChange : undefined}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onEdgeClick={handleEdgeClick}
        onNodeDragStop={handleNodeDragStop}
        nodeTypes={e2eFlowNodeTypes}
        edgeTypes={swimlaneEdgeTypes}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        nodesDraggable={editable}
        nodesConnectable={!readOnly}
        elementsSelectable={true}
        className="bg-slate-50 dark:bg-slate-900"
      >
        <Background variant={BackgroundVariant.Dots} gap={15} size={1} />
        <Controls showInteractive={!readOnly} />
        <MiniMap
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md"
        />

        {/* Issue #404: Stats panel and layout controls moved to page toolbar */}

        {/* Path Selector Panel */}
        {data.paths.length > 1 && (
          <Panel position="top-center" className="bg-white/95 dark:bg-slate-800/95 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Path:</span>
              <select
                value={selectedPathId || ''}
                onChange={(e) => selectPath(e.target.value)}
                className="text-xs border border-slate-200 dark:border-slate-700 rounded px-2 py-1 bg-white dark:bg-slate-800"
              >
                <option value="">All Paths</option>
                {data.paths.map((path) => (
                  <option key={path.id} value={path.id}>
                    {path.name || path.id} ({path.steps.length} steps)
                  </option>
                ))}
              </select>
            </div>
          </Panel>
        )}

        {/* Issue #404: Control Panel moved to page toolbar (Find Node, Auto Layout, Zoom) */}

        {/* Step Type Filter & Legend Panel - Issue #404: Moved to bottom-right */}
        {showLegend && (
          <Panel position="bottom-right" className="bg-white/95 dark:bg-slate-800/95 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-slate-700 max-w-sm">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Step Types</span>
                {activeStepTypes.size > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-blue-500 hover:text-blue-600"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {availableStepTypes.map((type) => {
                  const colors = getStepTypeColors(type);
                  const isActive = activeStepTypes.has(type);
                  const count = stats.stepsByType[type] || 0;

                  return (
                    <button
                      key={type}
                      onClick={() => toggleStepType(type)}
                      className={`
                        px-2 py-1 text-xs rounded-full border transition-all
                        ${isActive
                          ? 'ring-2 ring-offset-1 ring-blue-500'
                          : 'opacity-70 hover:opacity-100'
                        }
                      `}
                      style={{
                        backgroundColor: colors.bg,
                        borderColor: colors.border,
                        color: colors.text,
                      }}
                    >
                      {STEP_TYPE_LABELS[type]} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          </Panel>
        )}

        {/* Issue #354: Node Finder Panel - Issue #404: Moved to bottom-left to avoid overlap with legend */}
        {showNodeFinder && (
          <Panel position="bottom-left" className="bg-white/95 dark:bg-slate-800/95 rounded-md shadow-sm border border-slate-200 dark:border-slate-700 w-72 max-h-64 overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                🔍 Node Finder ({filteredNodes.length} nodes)
              </span>
              <button
                onClick={() => reactFlowInstance.fitView({ padding: 0.2 })}
                className="text-xs text-blue-500 hover:text-blue-600"
              >
                Fit All
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {filteredNodes.map((node) => {
                const stepType = node.data?.type as FlowStepType | undefined;
                const colors = stepType ? getStepTypeColors(stepType) : { bg: '#f1f5f9', border: '#94a3b8', text: '#475569' };
                const label = node.data?.label as string || node.id;
                const isHighlighted = node.id === highlightedNodeId;

                return (
                  <button
                    key={node.id}
                    onClick={() => focusNode(node.id)}
                    className={`
                      w-full text-left px-2 py-1.5 rounded text-xs transition-all
                      flex items-center gap-2 hover:ring-2 hover:ring-blue-300
                      ${isHighlighted ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/30' : ''}
                    `}
                    style={{
                      backgroundColor: isHighlighted ? undefined : colors.bg,
                      borderLeft: `3px solid ${colors.border}`,
                    }}
                    title={`Click to focus on: ${label}`}
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: colors.border }}
                    />
                    <span className="truncate" style={{ color: colors.text }}>
                      {label}
                    </span>
                    {stepType && (
                      <span className="ml-auto text-[10px] text-slate-400 flex-shrink-0">
                        {STEP_TYPE_LABELS[stepType]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
}

// Wrap with forwardRef
const E2EFlowRendererWithRef = forwardRef(E2EFlowRendererInner);

/**
 * E2E Flow Renderer with ReactFlowProvider
 * Wrapped in memo to prevent parent re-renders from cascading into React Flow,
 * which can break its internal drag state tracking.
 */
export const E2EFlowRenderer = memo(forwardRef<E2EFlowRendererHandle, E2EFlowRendererProps>(
  (props, ref) => {
    return (
      <ReactFlowProvider>
        <E2EFlowRendererWithRef {...props} ref={ref} />
      </ReactFlowProvider>
    );
  }
));

E2EFlowRenderer.displayName = 'E2EFlowRenderer';

export default E2EFlowRenderer;
